/**
 * Position verdict, as read on the card (Oct 2026, card direction B).
 *
 * The engine's fired rule leads, the action comes second, so the line reads as
 * the user's own rule firing ("Your profit target fired · profit 57% ≥ your 50%
 * line → Roll"), never as advice. Tone follows the KIND of rule, not the action:
 * reaching a goal is good news, the clock is neutral, and only protective stops
 * are red.
 */
import { DisciplineState, DisciplineTrigger } from '../types/discipline';

export type VerdictTone = 'goal' | 'clock' | 'risk' | 'quiet' | 'data';

const TONE: Record<DisciplineTrigger, VerdictTone> = {
  profit_target: 'goal',
  time_stop: 'clock',
  thesis_broken: 'risk',
  stock_stop: 'risk',
  option_stop: 'risk',
  structure_stop: 'risk',
  delta_breach: 'risk',
  earnings: 'risk',
  ex_dividend: 'risk',
  hold: 'quiet',
  stale_data: 'data',
  insufficient_data: 'data',
};

export const verdictTone = (d: DisciplineState): VerdictTone => TONE[d.trigger] ?? 'quiet';

const lowerFirst = (s: string) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

export interface VerdictLine {
  tone: VerdictTone;
  /** e.g. "Your profit target fired" / "No rule fired" */
  headline: string;
  /** e.g. "profit 57% ≥ your 50% line" / "nearest: …" */
  detail: string | null;
  /** The engine's action, shown after the arrow; null when nothing fired. */
  action: string | null;
  /** Other rules that fired at the same time, e.g. ["Time stop · 13 DTE / 21 DTE"]. */
  alsoFired: string[];
}

export function verdictLine(d: DisciplineState): VerdictLine {
  const tone = verdictTone(d);
  const rules = d.rules ?? [];

  if (tone === 'data') {
    return {
      tone,
      headline: d.trigger === 'stale_data' ? 'Prices too old to check your rules' : 'Not enough data to check your rules',
      detail: d.notes?.[0] ?? null,
      action: null,
      alsoFired: [],
    };
  }

  if (tone === 'quiet' || !d.fired_rule) {
    return {
      tone: 'quiet',
      headline: 'No rule fired',
      detail: d.flip_condition?.summary ? `nearest: ${lowerFirst(d.flip_condition.summary)}` : null,
      action: null,
      alsoFired: [],
    };
  }

  const alsoFired = rules
    .filter((r) => r.fired && r.name !== d.fired_rule!.name)
    .map((r) => `${r.name} · ${r.observed_display} / ${r.threshold_display}`);

  return {
    tone,
    headline: `Your ${lowerFirst(d.fired_rule.name)} fired`,
    detail: d.fired_rule.formula_human || null,
    action: d.action,
    alsoFired,
  };
}

/**
 * Urgency order for the positions grid: protective stops first, then any other
 * fired rule, then quiet positions, then positions we can't judge; inside a
 * tier, fewest days to expiry first. Unknown verdict (still loading) sorts with
 * the quiet tier so cards don't jump to the top while loading.
 */
export function urgencyTier(d: DisciplineState | null | undefined): number {
  if (!d) return 2;
  const tone = verdictTone(d);
  if (tone === 'risk') return 0;
  if (tone === 'goal' || tone === 'clock') return 1;
  if (tone === 'quiet') return 2;
  return 3;
}

export interface VerdictCounts {
  risk: number;
  clock: number;
  goal: number;
  quiet: number;
  data: number;
}

export function countVerdicts(list: Array<DisciplineState | null | undefined>): VerdictCounts {
  const c: VerdictCounts = { risk: 0, clock: 0, goal: 0, quiet: 0, data: 0 };
  for (const d of list) {
    if (!d) continue;
    c[verdictTone(d)] += 1;
  }
  return c;
}
